import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { ChevronDown, ChevronUp, FileText, Copy, Check } from 'lucide-react'
import { useCricketStore } from '#/store/cricketStore'
import { formatOvers, calculateEconomyRate, calculateStrikeRate } from '#/lib/utils'
import { exportScorecardAsPDF, shareScorecardToWhatsApp } from '#/lib/exportScorecard'
import { getMatchFn } from '#/server/match.functions'
import { useEffect } from 'react'
import type { InningData, DeliveryRecord } from '#/types/cricket'

export const Route = createFileRoute('/match_/$matchId/scorecard')({
  validateSearch: (search: Record<string, unknown>): { mode?: 'edit' | 'view'; token?: string } => {
    return {
      mode: (search.mode as 'edit' | 'view') || 'edit',
      token: (search.token as string) || '',
    }
  },
  loader: async ({ params }) => {
    // Check if match already exists in localStorage/Zustand store before waiting for remote network
    if (typeof window !== 'undefined') {
      try {
        const stored = localStorage.getItem('cricket-scorer-storage')
        if (stored) {
          const parsed = JSON.parse(stored)
          if (parsed?.state?.currentMatch?.id === params.matchId) {
            return { serverMatch: parsed.state.currentMatch }
          }
        }
      } catch {
        // Fall back to server function
      }
    }

    try {
      const res = await getMatchFn({ data: { id: params.matchId } })
      return { serverMatch: res.match || null }
    } catch {
      return { serverMatch: null }
    }
  },
  component: ScorecardScreen,
})

function ScorecardScreen() {
  const { matchId } = Route.useParams()
  const search = Route.useSearch()
  const loaderData = Route.useLoaderData()
  const navigate = useNavigate()
  const currentMatch = useCricketStore((state) => state.currentMatch)
  const savedMatches = useCricketStore((state) => state.savedMatches)
  const syncMatchState = useCricketStore((state) => state.syncMatchState)

  const match =
    currentMatch?.id === matchId
      ? currentMatch
      : savedMatches.find((m) => m.id === matchId) || loaderData?.serverMatch || null

  useEffect(() => {
    if (!currentMatch || currentMatch.id !== matchId) {
      if (loaderData?.serverMatch) {
        syncMatchState(loaderData.serverMatch)
      } else {
        const found = savedMatches.find((m) => m.id === matchId)
        if (found) syncMatchState(found)
      }
    }
  }, [matchId, currentMatch, savedMatches, loaderData, syncMatchState])

  const [activeTab, setActiveTab] = useState<'scoreboard' | 'overs'>('scoreboard')
  const [openInnings, setOpenInnings] = useState<{ [key: string]: boolean }>({
    innings1: false,
    innings2: true,
  })
  const [showExportMenu, setShowExportMenu] = useState(false)
  const [copiedMatchId, setCopiedMatchId] = useState(false)

  const displayMatchId = match.id.replace(/^match_/, '')

  const copyMatchId = async () => {
    if (!match?.id) return
    try {
      await navigator.clipboard.writeText(displayMatchId)
      setCopiedMatchId(true)
      setTimeout(() => setCopiedMatchId(false), 2000)
    } catch {
      // fallback
    }
  }

  if (!match) {
    return (
      <div className="p-6 text-center">
        <p className="text-gray-600 mb-4">Match data not available.</p>
        <button
          onClick={() => navigate({ to: '/' })}
          className="bg-[#2E7D32] text-white px-4 py-2 rounded-md font-medium"
        >
          Go Home
        </button>
      </div>
    )
  }

  const titleText = `${match.hostTeam} v/s ${match.visitorTeam.slice(0, 1)}...`
  const scoreSummaryText = `${match.battingTeam} ${match.totalRuns}/${match.totalWickets} (${formatOvers(
    match.legalBallsDelivered,
  )} ov)`

  // Gather deliveries for Overs tab across both innings
  // Screen 24 shows latest over at top (e.g. Ov 2 Guru to Givind, Ov 1 Bal to Givind & Givi, Ov 2 Surya..., Ov 1 Raja...)
  const allOversList: {
    key: string
    inningNumber: number
    overIndex: number
    deliveries: DeliveryRecord[]
  }[] = []

  const processInningOvers = (inningData: InningData | undefined, num: number) => {
    if (!inningData || !inningData.deliveryHistory) return
    const map: Record<number, DeliveryRecord[]> = {}
    inningData.deliveryHistory.forEach((del) => {
      if (!map[del.overIndex]) map[del.overIndex] = []
      map[del.overIndex].push(del)
    })
    const indices = Object.keys(map)
      .map(Number)
      .sort((a, b) => b - a)
    indices.forEach((idx) => {
      allOversList.push({
        key: `inn${num}_ov${idx}`,
        inningNumber: num,
        overIndex: idx,
        deliveries: map[idx],
      })
    })
  }

  // Push 2nd innings overs first (top), then 1st innings overs (bottom) matching Screen 24
  if (match.innings2) {
    processInningOvers(match.innings2, 2)
  } else if (match.currentInnings === 2) {
    processInningOvers(match, 2)
  }
  processInningOvers(match.innings1, 1)

  const renderInningAccordion = (inning: InningData, labelKey: 'innings1' | 'innings2', isDefaultOpen: boolean) => {
    const isOpen = openInnings[labelKey] ?? isDefaultOpen
    const toggle = () => setOpenInnings((prev) => ({ ...prev, [labelKey]: !isOpen }))

    return (
      <div key={labelKey} className="border-b border-gray-200">
        <div
          onClick={toggle}
          className="bg-[#2E7D32] text-white px-4 py-2.5 flex items-center justify-between cursor-pointer select-none"
        >
          <span className="font-bold text-sm tracking-wide">{inning.battingTeam}</span>
          <div className="flex items-center gap-2">
            <span className="font-bold text-sm">
              {inning.totalRuns}-{inning.totalWickets} ({formatOvers(inning.legalBallsDelivered)})
            </span>
            {isOpen ? <ChevronUp className="size-4" /> : <ChevronDown className="size-4" />}
          </div>
        </div>

        {isOpen && (
          <div>
            <div className="grid grid-cols-12 px-4 py-2 bg-gray-50 text-gray-600 font-semibold text-xs border-b border-gray-100">
              <div className="col-span-5">Batsman</div>
              <div className="col-span-1 text-center">R</div>
              <div className="col-span-1 text-center">B</div>
              <div className="col-span-1 text-center">4s</div>
              <div className="col-span-1 text-center">6s</div>
              <div className="col-span-3 text-right">SR</div>
            </div>

            {Object.values(inning.batsmen || {}).map((b) => (
              <div
                key={b.name}
                className="grid grid-cols-12 px-4 py-2 border-b border-gray-100 items-center text-sm"
              >
                <div className="col-span-5">
                  <div className="font-medium text-gray-900">{b.name}</div>
                  <div className="text-[11px] text-gray-500">
                    {b.isOut ? b.dismissal || 'out' : 'not out'}
                  </div>
                </div>
                <div className="col-span-1 text-center font-bold text-gray-900">{b.runs}</div>
                <div className="col-span-1 text-center text-gray-600">{b.balls}</div>
                <div className="col-span-1 text-center text-gray-600">{b.fours}</div>
                <div className="col-span-1 text-center text-gray-600">{b.sixes}</div>
                <div className="col-span-3 text-right text-gray-700 font-mono text-xs">
                  {calculateStrikeRate(b.runs, b.balls)}
                </div>
              </div>
            ))}

            {/* Extras line matching Screen 23 */}
            <div className="px-4 py-2 border-b border-gray-100 flex items-center justify-between text-xs text-gray-700 font-medium">
              <span>Extras</span>
              <span>
                {inning.extras.byes + inning.extras.legByes + inning.extras.wides + inning.extras.noBalls + inning.extras.penalty}{' '}
                {inning.extras.byes} B, {inning.extras.legByes} LB, {inning.extras.wides} WD, {inning.extras.noBalls} NB, {inning.extras.penalty} P
              </span>
            </div>

            {/* Total line */}
            <div className="px-4 py-2 bg-gray-50 border-b border-gray-200 flex items-center justify-between text-sm font-bold text-gray-900">
              <span>Total</span>
              <span>
                {inning.totalRuns}-{inning.totalWickets} ({formatOvers(inning.legalBallsDelivered)}){' '}
                <span className="text-xs font-normal text-gray-600 font-mono">
                  {((inning.totalRuns / (Math.max(1, inning.legalBallsDelivered) / 6))).toFixed(2)}
                </span>
              </span>
            </div>

            {/* Bowlers header */}
            <div className="grid grid-cols-12 px-4 py-1.5 bg-[#e8f2e8] text-gray-700 font-semibold text-xs border-b border-gray-200">
              <div className="col-span-5">Bowler</div>
              <div className="col-span-1 text-center">O</div>
              <div className="col-span-1 text-center">M</div>
              <div className="col-span-1 text-center">R</div>
              <div className="col-span-1 text-center">W</div>
              <div className="col-span-3 text-right">ER</div>
            </div>

            {Object.values(inning.bowlers || {}).map((bw) => (
              <div
                key={bw.name}
                className="grid grid-cols-12 px-4 py-2 border-b border-gray-100 items-center text-sm"
              >
                <div className="col-span-5 font-medium text-gray-900">{bw.name}</div>
                <div className="col-span-1 text-center text-gray-700">{formatOvers(bw.legalBalls)}</div>
                <div className="col-span-1 text-center text-gray-700">{bw.maidens}</div>
                <div className="col-span-1 text-center text-gray-700">{bw.runsConceded}</div>
                <div className="col-span-1 text-center font-bold text-gray-900">{bw.wickets}</div>
                <div className="col-span-3 text-right text-gray-700 font-mono text-xs">
                  {calculateEconomyRate(bw.runsConceded, bw.legalBalls)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="flex-1 flex flex-col min-h-screen bg-[#f5f6f8]">
      {/* Top Header Bar */}
      <header className="bg-[#2E7D32] text-white px-3 py-3 flex items-center justify-between sticky top-0 z-40">
        <div className="flex items-center gap-2">
          <button
            onClick={() =>
              navigate({
                to: '/match/$matchId',
                params: { matchId: match.id },
                search: search.mode ? { mode: search.mode, token: search.token } : undefined,
              })
            }
            className="p-1 hover:bg-white/10 rounded-full transition-colors active:scale-90"
            aria-label="Back"
          >
            <svg className="size-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
          </button>
          <span className="text-lg font-bold tracking-tight truncate max-w-[170px]">
            {titleText}
          </span>
        </div>

        <div className="flex items-center gap-2">
          {/* WhatsApp Direct Share */}
          <button
            onClick={() =>
              shareScorecardToWhatsApp(
                'printable-scorecard',
                `${match.hostTeam} vs ${match.visitorTeam}`,
                scoreSummaryText,
              )
            }
            className="p-1.5 hover:bg-white/10 rounded-full transition-colors active:scale-90 text-white"
            title="Share to WhatsApp"
          >
            <svg className="size-5 fill-current" viewBox="0 0 24 24">
              <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z"/>
            </svg>
          </button>

          {/* Direct PDF Export */}
          <button
            onClick={async () => {
              // Ensure both 1st and 2nd innings are open so full scorecard appears in the PDF
              setOpenInnings({ innings1: true, innings2: true })
              // Small delay to allow react re-render of accordions
              setTimeout(() => {
                exportScorecardAsPDF(
                  'printable-scorecard',
                  `${match.hostTeam}-vs-${match.visitorTeam}-scorecard.pdf`,
                )
              }, 150)
            }}
            className="p-1.5 hover:bg-white/10 rounded-full transition-colors active:scale-90 flex items-center gap-1 bg-black/20 border border-white/20 px-2.5 py-1 text-xs font-semibold"
            title="Download PDF Scorecard"
          >
            <FileText className="size-4 text-red-300" />
            <span className="text-[11px] font-bold">PDF</span>
          </button>
        </div>
      </header>

      {/* Segmented Tabs: Scoreboard vs Overs */}
      <div className="bg-[#2E7D32] text-white flex border-b border-[#1B5E20]">
        <button
          onClick={() => setActiveTab('scoreboard')}
          className={`flex-1 py-2.5 text-center font-semibold text-sm transition-all border-b-2 ${
            activeTab === 'scoreboard'
              ? 'border-white text-white'
              : 'border-transparent text-white/70 hover:text-white'
          }`}
        >
          Scoreboard
        </button>
        <button
          onClick={() => setActiveTab('overs')}
          className={`flex-1 py-2.5 text-center font-semibold text-sm transition-all border-b-2 ${
            activeTab === 'overs'
              ? 'border-white text-white'
              : 'border-transparent text-white/70 hover:text-white'
          }`}
        >
          Overs
        </button>
      </div>

      {/* Match ID Bar with Quick Copy for friends/viewers */}
      <div className="bg-[#1f2937] text-gray-200 px-4 py-2 flex items-center justify-between text-xs border-b border-gray-700 shadow-2xs">
        <span className="font-medium text-gray-300 flex items-center gap-1.5 truncate">
          <span>Match ID:</span>
          <span className="font-mono text-white select-all font-semibold">{displayMatchId}</span>
        </span>
        <button
          type="button"
          onClick={copyMatchId}
          className="flex items-center gap-1 px-2.5 py-1 rounded bg-white/10 hover:bg-white/20 active:scale-95 text-white transition-all cursor-pointer font-medium ml-2 shrink-0"
          title="Copy Match ID to share with friends"
        >
          {copiedMatchId ? (
            <>
              <Check className="size-3.5 text-emerald-400" />
              <span className="text-[11px] text-emerald-300">Copied</span>
            </>
          ) : (
            <>
              <Copy className="size-3.5" />
              <span className="text-[11px]">Copy ID</span>
            </>
          )}
        </button>
      </div>

      {/* Tab 1: Scoreboard Screen matching Screen 23 */}
      {activeTab === 'scoreboard' && (
        <div id="printable-scorecard" className="flex-1 bg-white">
          {/* Winner banner if match completed, otherwise toss note */}
          {match.winnerText ? (
            <div className="bg-[#eef3ee] text-gray-800 text-xs py-3 px-4 font-semibold border-b border-gray-200">
              {match.winnerText}
            </div>
          ) : (
            <div className="bg-[#eef3ee] text-gray-800 text-xs py-3 px-4 font-medium border-b border-gray-200">
              {match.tossWinner === 'host' ? match.hostTeam : match.visitorTeam} won the toss and opted to{' '}
              {match.tossDecision === 'bat' ? 'Bat' : 'Bowl'} first.
            </div>
          )}

          {/* Innings 1 Accordion */}
          {renderInningAccordion(match.innings1, 'innings1', !match.innings2)}

          {/* Innings 2 Accordion (if started/completed) */}
          {match.innings2 && renderInningAccordion(match.innings2, 'innings2', true)}
        </div>
      )}

      {/* Tab 2: Overs Breakdown Screen matching Screen 24 */}
      {activeTab === 'overs' && (
        <div className="flex-1 p-3 space-y-3">
          {allOversList.length === 0 ? (
            <p className="text-center text-gray-500 py-10 text-sm">No completed overs yet.</p>
          ) : (
            allOversList.map((item) => {
              const bowlerName = item.deliveries[0]?.bowler || 'Bowler'
              const batsmenInvolved = Array.from(
                new Set(item.deliveries.flatMap((d) => [d.striker, d.nonStriker])),
              ).join(' & ')
              const overRuns = item.deliveries.reduce((acc, d) => acc + d.runsScored, 0)

              return (
                <div
                  key={item.key}
                  className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 space-y-2.5"
                >
                  <div className="flex items-center justify-between text-xs text-gray-600 font-semibold">
                    <span className="text-gray-900 font-bold">Ov {item.overIndex}</span>
                    <span className="text-gray-700">
                      {bowlerName} to {batsmenInvolved}
                    </span>
                  </div>

                  <div className="flex items-center justify-between gap-3 pt-1">
                    <span className="text-sm font-bold text-gray-900 shrink-0">{overRuns} Runs</span>
                    <div className="flex items-center gap-2 overflow-x-auto py-1 scrollbar-none max-w-full justify-end">
                      {item.deliveries.map((del) => {
                        const isFour = del.runsScored === 4 && del.extraType === 'none'
                        const isSix = del.runsScored === 6 && del.extraType === 'none'
                        const isWkt = del.isWicket

                        return (
                          <span
                            key={del.id}
                            className={`size-7.5 shrink-0 rounded-full flex items-center justify-center text-xs font-bold border shadow-xs ${
                              isWkt
                                ? 'bg-red-600 text-white border-red-700'
                                : isSix
                                ? 'bg-[#2E7D32] text-white border-[#1B5E20]'
                                : isFour
                                ? 'bg-[#E65100] text-white border-orange-600'
                                : 'bg-white text-gray-800 border-gray-300'
                            }`}
                          >
                            {del.displayBadge}
                          </span>
                        )
                      })}
                    </div>
                  </div>
                </div>
              )
            })
          )}
        </div>
      )}
    </div>
  )
}
